import axios, { AxiosError, AxiosInstance, CreateAxiosDefaults, InternalAxiosRequestConfig } from "axios";
import { IAuthError, IAuthResponse, ILoginCredentials } from "@/types/auth";
import { ISession } from "@/types/ISession";
import { IInstallmentDetail } from "@/types/IInstallment";
import { IGlobalWarning, IPaymentProviderBackend, IProvidersResponse } from "@/types/IPaymentProvider";
import {
  IReference,
  IReferenceRequest,
  IReferenceResponse,
  IBitsoReferenceRequest,
  IBitsoReferenceResponse,
} from "@/types/IReferenceResponse";
import {
  ISuperPacmanFetchInstallmentRequest,
  ISuperPacmanInstallment,
  ISuperPacmanReferenceRequest,
  ISuperPacmanReferenceResponse,
  ISuperPacmanVirtualAccountNumberRequest,
  ISuperPacmanVirtualAccountNumberResponse,
  IXenditReferenceRequest,
  IXenditReferenceResponse,
} from "@/types/ISuperPacman";
import { formatDate } from "@/shared-helpers/date-time.helper";
import { IActionLogsRequest, IActionLogsResponse } from "@/types/IActionLogs";
import { VueGtag } from "vue-gtag-next";
import { logGTagEvent } from "@/helpers/google-analytics.helpers";
import { Providers } from "@/types/Providers";
import type { IWompiAcceptanceTokenResponse, IWompiTransactionResponse } from "@/types/IWompiResponse";
import { snakeToCamel } from "@/shared-helpers/snake-to-camel.helper";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const SESSION_KEY = "session_data";
const SHARED_PASSWORD = process.env.VUE_APP_MIDDLEWARE_SHARED_SECRET || "";

const BASE_URL: string = process.env.VUE_APP_MIDDLEWARE_URL || "";
const LOGIN_URL: string = process.env.VUE_APP_MIDDLEWARE_AUTH || "";
const REFRESH_URL: string = process.env.VUE_APP_MIDDLEWARE_REFRESH || "";
const PROVIDERS_URL: string = process.env.VUE_APP_MIDDLEWARE_PROVIDERS || "";
const REFERENCE_URL: string = process.env.VUE_APP_MIDDLEWARE_REFERENCE || "";
const LOG_URL: string = process.env.VUE_APP_MIDDLEWARE_LOGS_URL || "";

class AuthHelper {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: BASE_URL,
      timeout: 10000,
    } as CreateAxiosDefaults) as AxiosInstance;

    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      },
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = this.getRefreshToken();
            if (!refreshToken) {
              await this.logout();
              throw new IAuthError("No refresh token available", 401);
            }

            const response = await axios.post<IAuthResponse>(
              `${BASE_URL}${REFRESH_URL}`,
              { refresh: refreshToken }, // Updated to match API expectation
            );

            const { access, refresh } = response.data;

            this.setToken(access);
            this.setRefreshToken(refresh);

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${access}`;
            }
            return this.api(originalRequest);
          } catch (refreshError) {
            await this.logout();
            throw new IAuthError("Failed to refresh token", (refreshError as AxiosError).response?.status);
          }
        } else if (
          error.response?.status === 400 &&
          error.response?.data &&
          typeof error.response.data === "object" &&
          error.response.data["error_details"]
        ) {
          // We've received details about the declined transaction
          return Promise.resolve(error.response);
        }
        throw new IAuthError(error.message, error.response?.status, error.response?.data);
      },
    );
  }

  async login(credentials: ILoginCredentials): Promise<IAuthResponse> {
    try {
      const response = await this.api.post<IAuthResponse>(LOGIN_URL, credentials);
      const { access, refresh, session } = response.data;

      this.setToken(access);
      this.setRefreshToken(refresh);
      this.setSession(session);

      return response.data as IAuthResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new IAuthError("Login failed: " + error.message, (error as AxiosError).response?.status);
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      // Optional: Call backend to invalidate token
      // await this.api.post('/auth/logout');
    } finally {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(SESSION_KEY);
    }
  }

  private setToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  private setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  private setSession(session: ISession): void {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  getSession(): ISession | null {
    const session = localStorage.getItem(SESSION_KEY);
    return session ? JSON.parse(session) : null;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Helper method to get installment information
  getInstallments(): IInstallmentDetail[] | null {
    const session = this.getSession();
    return session?.installment.installments ?? null;
  }

  // Helper method to get current currency
  getCurrency(): { currency: string; symbol: string } | null {
    const session = this.getSession();
    if (!session) return null;
    return {
      currency: session.installment.currency,
      symbol: session.installment.currencySymbol,
    };
  }

  async getProviders(): Promise<{ providers: IPaymentProviderBackend[]; globalWarning: IGlobalWarning | null }> {
    const maxAttempts = 3;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        if (!this.isAuthenticated()) {
          throw new IAuthError("User not authenticated", 401);
        }

        const response = await this.api.get<IProvidersResponse>(PROVIDERS_URL);
        return {
          providers: response.data.results as IPaymentProviderBackend[],
          globalWarning: response.data.global_warning ?? null,
        };
      } catch (error) {
        if (error instanceof Error) {
          console.error(`Attempt ${attempts + 1} failed: ${error.message}`);
          if (attempts === maxAttempts - 1) {
            throw new IAuthError(
              "Failed to fetch providers after multiple attempts: " + error.message,
              (error as AxiosError).response?.status,
            );
          }
        }
      }
      attempts++;
    }
    throw new IAuthError("Failed to fetch providers after multiple attempts", 500);
  }

  async getReference(params: IReferenceRequest): Promise<IReference> {
    const maxAttempts = 3;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        if (!this.isAuthenticated()) {
          throw new IAuthError("User not authenticated", 401);
        }
        const response = await this.api.post<IReferenceResponse>(REFERENCE_URL, params);
        const responseData = snakeToCamel<IReferenceResponse>(response.data);
        if (!responseData.reference && responseData.errorDetails) {
          throw new IAuthError(responseData.errorDetails.messages.toString(), 400, responseData.errorDetails);
        }
        const validThrough = responseData.reference.expirationTime as unknown as number;
        if (validThrough) {
          responseData.reference.expirationTime = formatDate(validThrough);
        } else {
          responseData.reference.expirationTime = "";
        }

        return responseData.reference;
      } catch (error) {
        if (error instanceof AxiosError) {
          console.error(`Attempt ${attempts + 1} failed: ${error.message}`);
          if (attempts === maxAttempts - 1) {
            throw new IAuthError(
              "Failed to fetch reference after multiple attempts: " + error.message,
              (error as AxiosError).response?.status,
            );
          }
        } else if (error instanceof IAuthError) {
          if (params.paymentType === Providers.CODI && error.statusCode === 400) {
            return {
              referenceCode: "",
              deviceTag: params.deviceTag,
              countryCode: params.countryCode || "",
              paymentType: params.paymentType,
              errorDetails: error.response,
            };
          }
        }
      }
      attempts++;
    }
    throw new IAuthError("Failed to fetch reference after multiple attempts", 500);
  }

  /**
   * Fetches a virtual account number for the user.
   */
  async getVirtualAccountNumber(
    params: ISuperPacmanVirtualAccountNumberRequest,
  ): Promise<ISuperPacmanVirtualAccountNumberResponse> {
    const args = new URLSearchParams({
      sharedSecret: SHARED_PASSWORD,
      financeProductType: params.financeProductType,
      financeProductId: params.financeProductId.toString(),
    });
    const response = await this.api.get(`super-pacman/virtual-account-number/?${args}`);
    return snakeToCamel<ISuperPacmanVirtualAccountNumberResponse>(response.data);
  }

  /**
   * Fetches the pre-signed acceptance token for Wompi.
   */
  async getWompiPreSignedAcceptanceToken(): Promise<IWompiAcceptanceTokenResponse> {
    const response = await this.api.get<IWompiAcceptanceTokenResponse>(
      `super-pacman/wompi-acceptance-token/?sharedSecret=${SHARED_PASSWORD}`,
    );
    return snakeToCamel<IWompiAcceptanceTokenResponse>(response.data);
  }

  async logAction(payload: IActionLogsRequest, gTag: VueGtag | null = null): Promise<IActionLogsResponse> {
    // TODO: VPOP-234 Add env validation inside, and an optional bypass argument to force logs.
    try {
      if (!this.isAuthenticated()) throw new IAuthError("User not authenticated", 401);

      const response = await this.api.post(LOG_URL, payload);
      if (gTag) {
        let data;
        if (payload.data) {
          data = payload.data;
        } else {
          data = payload;
        }
        logGTagEvent(gTag, payload.action, {
          event_category: payload.actionType,
          event_label: payload.action,
          value: data,
        });
      }
      return response.data as IActionLogsResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new IAuthError("Failed to log action: " + error.message, (error as AxiosError).response?.status);
      }
      throw error;
    }
  }

  async fetchSuperPacmanInstallments(request: ISuperPacmanFetchInstallmentRequest): Promise<ISuperPacmanInstallment> {
    try {
      const response = await this.api.post("super-pacman/installments/", request);
      const { access, refresh } = response.data;

      this.setToken(access);
      this.setRefreshToken(refresh);

      return response.data.data as ISuperPacmanInstallment;
    } catch (error) {
      if (error instanceof Error) {
        throw new IAuthError("Login failed: " + error.message, (error as AxiosError).response?.status);
      }
      throw error;
    }
  }

  /**
   * Fetches the reference from Super Pacman.
   */
  async fetchSuperPacmanReference<T = ISuperPacmanReferenceResponse>(
    request: ISuperPacmanReferenceRequest,
  ): Promise<T> {
    try {
      request["sharedSecret"] = SHARED_PASSWORD;
      const response = await this.api.post("super-pacman/reference/", request);
      if (response?.data) {
        return snakeToCamel<T>(response.data);
      } else {
        throw new Error("Empty response data");
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Creates a Xendit payment request for the specified channel.
   * Proxies to the xendit_integration service via the middleware.
   */
  async fetchXenditReference(request: IXenditReferenceRequest): Promise<IXenditReferenceResponse> {
    const payload = { ...request, sharedSecret: SHARED_PASSWORD };
    const response = await this.api.post("xendit-reference/", payload);
    if (response?.data) {
      return snakeToCamel<IXenditReferenceResponse>(response.data);
    }
    throw new Error("Empty response data");
  }

  /**
   * Fetches the PSE financial institutions.
   */
  async fetchPSEFinancialInstitutions(): Promise<{ label: string; value: string }[]> {
    try {
      const response = await this.api.get("super-pacman/pse-financial-institutions/");
      return response.data.map((institution: Record<string, string>) => ({
        label: institution["financial_institution_name"],
        value: institution["financial_institution_code"],
      }));
    } catch (error) {
      if (error instanceof Error) {
        throw new IAuthError(
          "Failed to fetch PSE financial institutions: " + error.message,
          (error as AxiosError).response?.status,
        );
      }
      throw error;
    }
  }

  /**
   * Fetches the transaction details from Wompi.
   */
  async getWompiTransaction(transactionId: string): Promise<IWompiTransactionResponse> {
    const response = await this.api.get(`super-pacman/wompi-get-transaction/${transactionId}/`);
    return snakeToCamel<IWompiTransactionResponse>(response.data);
  }

  /**
   * Gets or creates a Bitso CLABE reference for the user.
   * This calls the middleware which proxies to bitso_integration service.
   */
  async getBitsoReference(params: IBitsoReferenceRequest): Promise<IReference> {
    const maxAttempts = 3;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        // Call middleware endpoint (NOT bitso_integration directly)
        // Send sharedSecret as header to ensure it's available during authentication
        if (!this.isAuthenticated()) {
          throw new IAuthError("User not authenticated", 401);
        }
        const response = await this.api.post<IBitsoReferenceResponse>(
          "super-pacman/bitso-reference/",
          {
            userId: params.userId,
            customerNationalIdNumber: params.customerNationalIdNumber,
            customerNationalIdType: params.customerNationalIdType,
            customerNationalIdCountry: params.customerNationalIdCountry,
          },
          {
            headers: {
              "X-Sharedsecret": SHARED_PASSWORD,
            },
          },
        );

        const responseData = snakeToCamel<IBitsoReferenceResponse>(response.data);

        return {
          referenceCode: responseData.clabe || "",
          deviceTag: params.deviceTag,
          countryCode: params.countryCode || "",
          paymentType: Providers.BITSO,
        };
      } catch (error) {
        if (error instanceof AxiosError) {
          console.error(`Attempt ${attempts + 1} failed: ${error.message}`);
          if (attempts === maxAttempts - 1) {
            throw new IAuthError(
              "Failed to fetch Bitso reference after multiple attempts: " + error.message,
              (error as AxiosError).response?.status,
            );
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * (attempts + 1)));
        } else {
          throw error;
        }
      }
      attempts++;
    }
    throw new IAuthError("Failed to fetch Bitso reference after multiple attempts", 500);
  }
}

// Create singleton instance
const authHelper = new AuthHelper();
export default authHelper;
